import { useToast } from '@chakra-ui/react';
import { useDaily, useRecording } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { JobEntity } from '@shared/db';
import { logContext } from '@shared/services';
import { AppointmentStatus } from '@shared/types';
import { computeEndSessionReason } from '@web-app/components/CallV2/EndCallModal/endSessionReason';
import { SHORT_API_REQUEST_TIMEOUT_MS } from '@web-app/constants/timeouts';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useProviderCallContext } from '@web-app/context/ProviderCallContext';
import { useEndCall } from '@web-app/hooks/mutations/appointments/endCall';
import { useAppointmentStatus } from '@web-app/hooks/mutations/healthie/useAppointmentStatus';
import { useSaveSessionNoteImmediate } from '@web-app/hooks/mutations/session-notes/saveSessionNote';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { Note } from '@web-app/server/api/types';
import { getSessionNoteData } from '@web-app/utils/api/session-notes/utils';
import { withTimeout } from '@web-app/utils/api/timeout';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import {
  clearActiveCallData,
  clearHealthiePayload,
  clearPendingSessionData,
  setPendingSessionData,
} from '@web-app/utils/session-beacon';
import { useCallback, useMemo, useState } from 'react';

import { useEjectParticipants } from './queries/eject-participants-by-room-name';

interface UseEndAppointmentArgs {
  roomId: string;
  appointment: AppointmentResponse | undefined;
  getSessionNote: () => Partial<Note> | undefined;
  /** Enable mock transcript for e2e testing in staging */
  useMockTranscript?: boolean;
  // Duration values now come from ProviderCallContext
}

export interface UseEndAppointmentReturn {
  // Modal state
  /** Whether the end appointment modal is currently open */
  isModalOpen: boolean;
  /** Currently selected appointment status (null if none selected) */
  selectedStatus: AppointmentStatus | null;
  /** Whether appointment status is currently being updated in backend */
  isUpdatingStatus: boolean;
  /** Whether the appointment occurred on the phone (not via video) */
  apptOccurredOnThePhone: boolean;

  // SOAP note generation state
  /** The SOAP note job entity if generation was triggered, null otherwise */
  soapNoteJob: JobEntity | null;
  /** Error message if SOAP note generation failed, null otherwise */
  soapNoteError: string | null;
  /** Whether SOAP note generation is currently in progress */
  isSoapNoteLoading: boolean;
  /** Whether SOAP notes will be generated based on selected status (true if status is "Occurred") */
  willGenerateSoapNotes: boolean;

  // Actions
  /** Opens the end appointment modal and calculates duration */
  openModal: () => void;
  /** Closes the end appointment modal (cancel) */
  closeModal: () => void;
  /** Sets the appointment status selection */
  selectStatus: (status: AppointmentStatus | null) => void;
  /** Sets whether the appointment occurred on the phone */
  setApptOccurredOnThePhone: (occurred: boolean) => void;
  /** Confirms and executes the end call workflow */
  confirmEndCall: () => Promise<void>;

  // Validation
  /** Whether the confirm button should be enabled (true if a status is selected) */
  canConfirm: boolean;
}

/**
 * Hook to manage end appointment workflow.
 *
 * Orchestrates:
 * 1. Modal open/close
 * 2. Status selection
 * 3. Duration calculation (via ProviderCallContext)
 * 4. Session note saving
 * 5. Appointment status update (both DBs)
 * 6. Leaving Daily call
 *
 * Gets duration values from ProviderCallContext to avoid prop drilling.
 * Must be used within ProviderCallContextController.
 *
 * @see ProviderCallContextController - Required wrapper component
 */
export function useEndAppointment({
  roomId,
  appointment,
  getSessionNote,
  useMockTranscript,
}: UseEndAppointmentArgs): UseEndAppointmentReturn {
  // Get duration values from context (organized by domain)
  const { duration, lifecycle } = useProviderCallContext();
  const {
    calculatedDuration,
    editedDuration,
    finalDuration,
    calculateDuration,
  } = duration;
  const { momentBothParticipantsJoined } = lifecycle;

  // Local state - just modal visibility and selected status
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] =
    useState<AppointmentStatus | null>(null);
  const [apptOccurredOnThePhone, setApptOccurredOnThePhone] =
    useState<boolean>(false);

  const { trackEvent } = useAnalytics();
  const callObject = useDaily();
  const { stopRecording, isRecording } = useRecording();
  const { mutate: ejectParticipants } = useEjectParticipants();
  const willGenerateSoapNotes = useMemo(
    () => selectedStatus === AppointmentStatus.Occurred,
    [selectedStatus],
  );
  const toast = useToast();

  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
    useAppointmentStatus();

  const { mutate: saveNoteImmediate } = useSaveSessionNoteImmediate({
    onSuccess: (data: Note) => {
      datadogLogs.logger.info(
        'Provider notes saved successfully',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
          appointmentId: appointment?.healthie_id,
          sessionNoteId: data.sessionNote?.id,
        }),
      );
    },
  });

  const {
    mutateAsync: triggerSoapNoteGenerationAsync,
    data: soapNoteData,
    error: soapNoteError,
    isPending: isSoapNoteLoading,
  } = useEndCall();

  /**
   * Open modal and trigger duration calculation
   */
  const openModal = useCallback(() => {
    // Calculate duration when modal opens (matches existing behavior)
    calculateDuration();

    setIsModalOpen(true);

    datadogLogs.logger.info(
      'Modal opened',
      callLogContext('end-appointment', {
        roomId,
        participantType: 'provider',
        momentBothParticipantsJoined:
          momentBothParticipantsJoined?.toISOString(),
        appointmentId: appointment?.healthie_id,
      }),
    );
  }, [
    calculateDuration,
    roomId,
    momentBothParticipantsJoined,
    appointment?.healthie_id,
  ]);

  /**
   * Close modal (cancel)
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    trackEvent(EVENT_NAMES.CLICKS_RETURN_TO_CALL, { roomId });
  }, [roomId, trackEvent]);

  /**
   * End the Daily call - stops recording and leaves the call
   * Called immediately at start of end call workflow
   */
  const endDailyCall = useCallback(async () => {
    datadogLogs.logger.info(
      'Ending Daily call',
      callLogContext('end-appointment', {
        roomId,
        participantType: 'provider',
        appointmentId: appointment?.healthie_id,
        selectedStatus,
        isRecording,
      }),
    );

    // Stop recording before leaving
    if (isRecording) {
      try {
        stopRecording();
        datadogLogs.logger.info(
          'Recording stopped',
          callLogContext('end-appointment', {
            roomId,
            participantType: 'provider',
            appointmentId: appointment?.healthie_id,
            selectedStatus,
          }),
        );
      } catch (recordingError) {
        // Errors here are probably not a big deal, as recording will automatically stop
        // when the call is completed. This will just speed things along.
        datadogLogs.logger.warn(
          'Error stopping recording',
          callLogContext('end-appointment', {
            roomId,
            participantType: 'provider',
            appointmentId: appointment?.healthie_id,
            selectedStatus,
          }),
          recordingError instanceof Error ? recordingError : undefined,
        );
      }
    }

    // Stop transcription before leaving to ensure final VTT is flushed
    try {
      callObject?.stopTranscription();
    } catch {
      datadogLogs.logger.warn(
        'Error stopping transcription',
        callLogContext('transcription', {
          roomId,
          participantType: 'provider',
          appointmentId: appointment?.healthie_id,
        }),
      );
    }

    // This triggers 'left-meeting' event which can trigger post-call UI
    try {
      await callObject?.leave();
      datadogLogs.logger.info(
        'Left Daily call',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
          appointmentId: appointment?.healthie_id,
          selectedStatus,
        }),
      );

      ejectParticipants(String(roomId));
      datadogLogs.logger.info(
        'Ejected participants',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
          selectedStatus,
        }),
      );
    } catch (err) {
      datadogLogs.logger.error(
        'Failed to leave Daily call',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
          appointmentId: appointment?.healthie_id,
        }),
        err instanceof Error ? err : new Error(String(err)),
      );
      // Continue execution - leaving the call shouldn't block SOAP note generation
    }
  }, [
    roomId,
    selectedStatus,
    isRecording,
    stopRecording,
    appointment?.healthie_id,
    callObject,
    ejectParticipants,
  ]);

  /**
   * Confirm end call - orchestrates entire workflow:
   * 1. Track analytics
   * 2. Save session notes (if any)
   * 3. Update appointment status (both DBs)
   * 4. Leave Daily call
   */
  const confirmEndCall = useCallback(async () => {
    if (!selectedStatus) {
      datadogLogs.logger.warn(
        'Cannot confirm - no status selected',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
        }),
      );
      return;
    }

    // Reaching this callback with a non-null reason means the provider
    // accepted the StatusConfirmationModal warning. Log it so we can see in
    // Datadog how often providers overwrite a saved status vs confirm a
    // duration mismatch on the first save. The trigger is computed via the
    // same helper as `EndCallModalV2.handleEndSession` to keep them in sync.
    const previousStatus =
      (appointment?.status as AppointmentStatus | null | undefined) ?? null;
    const confirmationReason = computeEndSessionReason({
      selectedStatus,
      currentStatus: previousStatus,
      calculatedDuration,
    });
    if (confirmationReason !== null) {
      datadogLogs.logger.info(
        'End session status confirmation accepted',
        logContext('video-call', 'end-appointment', {
          reason: confirmationReason,
          roomId,
          appointmentId: appointment?.healthie_id,
          previousStatus,
          selectedStatus,
          calculatedDuration,
        }),
      );
    }

    // Track analytics with audit data
    const auditData =
      editedDuration !== null && editedDuration !== calculatedDuration
        ? {
            providerId: appointment?.provider_id,
            clientId: appointment?.client_id,
            appointmentId: appointment?.healthie_id,
            initialDuration: calculatedDuration,
            editedDuration: editedDuration,
          }
        : {};

    trackEvent(EVENT_NAMES.CLICK_END_SESSION_BUTTON, {
      roomId,
      status: selectedStatus,
      duration: finalDuration,
      apptOccurredOnThePhone,
      callExperienceVersion: 2,
      ...auditData,
    });

    // Disable forced beacon — provider confirmed End Session, use exact data
    clearActiveCallData();

    // Store pending data for sendBeacon fallback (in case user closes tab)
    // Uses global module-level variable that survives React lifecycle
    setPendingSessionData({
      roomId,
      duration: finalDuration,
      apptOccurredOnThePhone,
      status: selectedStatus,
      // Healthie payload for second beacon (provider ID will be looked up on backend)
      healthiePayload: appointment?.healthie_id
        ? {
            appointmentId: appointment.healthie_id,
            status: selectedStatus,
            duration: String(finalDuration),
            roomId,
          }
        : undefined,
    });

    await endDailyCall();

    try {
      // Step 1: Save session notes (if any)
      const sessionNote = getSessionNote();
      if (sessionNote?.sessionNote) {
        const noteData = getSessionNoteData({ sessionNote });
        saveNoteImmediate(noteData);

        datadogLogs.logger.info(
          'Saving session notes',
          callLogContext('end-appointment', {
            roomId,
            participantType: 'provider',
            appointmentId: appointment?.healthie_id,
          }),
        );
      }

      // Step 2: Update appointment status (both databases)
      if (appointment?.healthie_id && appointment?.provider_id && roomId) {
        const updatePayload = {
          appointmentId: appointment.healthie_id,
          roomName: roomId,
          status: selectedStatus,
          providerUserId: appointment.provider_id,
          clientUserId: appointment.client_id,
          duration: String(finalDuration), // String for Healthie API
          chartingSavedDirectlyInHealthie: false,
        };

        datadogLogs.logger.info(
          'End Call payload',
          callLogContext('end-appointment', {
            roomId,
            participantType: 'provider',
            appointmentId: appointment?.healthie_id,
            payload: {
              ...updatePayload,
              initialDuration: String(calculatedDuration),
              editedDuration: String(editedDuration),
            },
          }),
        );

        try {
          await updateStatus(updatePayload);
          // Clear Healthie payload from beacon data - Healthie already updated
          clearHealthiePayload();
        } catch (error) {
          // Don't clear healthiePayload - let beacon retry Healthie update
          toast({
            title: 'Error updating appointment status',
            description:
              'There was an error updating the appointment status. Please update the status directly in Healthie.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          datadogLogs.logger.error(
            'Background update status error',
            callLogContext('end-appointment', {
              roomId,
              participantType: 'provider',
              appointmentId: appointment?.healthie_id,
            }),
            error instanceof Error ? error : new Error(String(error)),
          );
        }

        if (willGenerateSoapNotes) {
          // Wait for session details to be saved (includes loggedSessionDuration)
          // Use timeout to prevent blocking user indefinitely if request hangs
          // If timeout/error occurs, cron job will pick up abandoned calls later
          try {
            await withTimeout(
              triggerSoapNoteGenerationAsync({
                roomId,
                duration: finalDuration,
                apptOccurredOnThePhone,
                providerEmail: appointment?.healthie?.provider?.email,
                useMockTranscript,
              }),
              'End call session save',
              SHORT_API_REQUEST_TIMEOUT_MS,
            );

            // Clear pending data - request succeeded
            clearPendingSessionData();

            datadogLogs.logger.info(
              'SOAP note generation triggered',
              callLogContext('end-appointment', {
                roomId,
                participantType: 'provider',
                appointmentId: appointment?.healthie_id,
                jobId: soapNoteData?.soapNotes?.id,
              }),
            );
          } catch (endCallError) {
            // Don't clear pendingSessionData - let beforeunload send via beacon
            // Log error but continue - cron job will pick up abandoned calls
            datadogLogs.logger.error(
              'Failed to save session data (will be picked up by cron)',
              callLogContext('end-appointment', {
                roomId,
                participantType: 'provider',
                appointmentId: appointment?.healthie_id,
              }),
              endCallError instanceof Error
                ? endCallError
                : new Error(String(endCallError)),
            );
          }
        } else {
          datadogLogs.logger.info(
            'SOAP note generation skipped',
            callLogContext('end-appointment', {
              roomId,
              participantType: 'provider',
              appointmentId: appointment?.healthie_id,
              selectedStatus,
            }),
          );
        }
      }
    } catch (error) {
      datadogLogs.logger.error(
        'Error during end call workflow',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
          appointmentId: appointment?.healthie_id,
          selectedStatus,
          duration: finalDuration,
          calculatedDuration,
          editedDuration,
          isRecording,
        }),
        error instanceof Error ? error : new Error(String(error)),
      );
      // Log error but continue - modal will close and cron job will handle abandoned calls
    }

    setIsModalOpen(false);
  }, [
    selectedStatus,
    editedDuration,
    calculatedDuration,
    appointment,
    trackEvent,
    roomId,
    finalDuration,
    endDailyCall,
    getSessionNote,
    saveNoteImmediate,
    updateStatus,
    isRecording,
    triggerSoapNoteGenerationAsync,
    soapNoteData?.soapNotes?.id,
    toast,
    apptOccurredOnThePhone,
    willGenerateSoapNotes,
    useMockTranscript,
  ]);

  return {
    // State
    isModalOpen,
    selectedStatus,
    isUpdatingStatus,
    apptOccurredOnThePhone,

    // SOAP note generation state (from TanStack Query mutation)
    soapNoteJob: soapNoteData?.soapNotes ?? null,
    soapNoteError:
      soapNoteError instanceof Error ? soapNoteError.message : null,
    isSoapNoteLoading,

    // Actions
    openModal,
    closeModal,
    selectStatus: setSelectedStatus,
    setApptOccurredOnThePhone,
    confirmEndCall,
    willGenerateSoapNotes,

    // Validation
    canConfirm: selectedStatus !== null,
  };
}
